import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useUser } from '@hooks/useUser';
import {
  getOperationSummary,
  getOperationIncidents,
  OperationSummary,
  OperationIncident
} from '../../../networkRequest/operationsService';
import { PageNavigation, Toast } from '@components';
import OperationDate from '../components/OperationDate';
import OperationsSelect from '../components/OperationsSelect';
import OperationOfficialAvatar from '../components/OperationOfficialAvatar';
import CreateIncidentDialog from './components/CreateIncidentDialog';
import { Add } from '@mui/icons-material';
import { COPY, PAGE_PATHS } from '../../../constants';

const orderByOptions = [
  { value: 'performed_at_desc', label: 'Most recent' },
  { value: 'title_asc', label: 'Title' }
];

const OperationIncidentsPage: React.FC = () => {
  const { operationId } = useParams<{ operationId: string }>();
  const { user } = useUser();
  const [ loading, setLoading ] = useState<boolean>(false);
  const [ operation, setOperation ] = useState<OperationSummary | null>(null);
  const [ incidents, setIncidents ] = useState<OperationIncident[]>([]);
  const [ orderBy, setOrderBy ] = useState<string>(orderByOptions[0].value);
  const [ error, setError ] = useState<string | null>(null);
  const [ createIncidentDialogOpen, setCreateIncidentDialogOpen ] = useState<boolean>(false);

  const _fetchOperationIncidents = useCallback(
    async () => {
      if (!operationId || !user?.accessToken) {
        return;
      }
      const { data, error } = await getOperationIncidents({ accessToken: user.accessToken, operationId, orderBy });
      if (data) {
        setIncidents(data);
      } else {
        setError(error ?? 'Failed to load operation incidents');
      }
    },
    [ operationId, user?.accessToken, orderBy ]
  );

  useEffect(
    () => {
      if (!operationId || !user?.accessToken) {
        return;
      }

      // Load operation and incidents
      const fetchData = async () => {
        const fetchOperation = async () => {
          const { data, error } = await getOperationSummary(user.accessToken, operationId);
          if (data) {
            setOperation(data);
          } else {
            setError(error ?? 'Failed to load operation');
          }
        };

        setLoading(true);
        setError(null);
        try {
          await Promise.all([
            fetchOperation(),
            _fetchOperationIncidents()
          ]);
        } catch {
          setError('Failed to load operation incidents data');
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    },
    [ operationId, user?.accessToken, orderBy, _fetchOperationIncidents ]
  );

  const handleCreateIncident = async () => {
    fetchOperationIncidents();
  };

  const fetchOperationIncidents = async () => {
    try {
      setLoading(true);
      await _fetchOperationIncidents();
    }
    finally {
      setLoading(false);
    }
  };

  if (!operationId || !user?.accessToken) {
    return null;
  }

  return (
    <>
      <PageNavigation
        breadcrumbs={[
          { display: COPY.titles.operations, path: PAGE_PATHS.operations },
          { display: operation?.title ?? '', path: `${PAGE_PATHS.operations}/${operationId}` }
        ]}
        controls={[
          <Button
            sx={{ mt: '-15px', mb: '-12px' }}
            variant="contained"
            startIcon={<Add />}
            onClick={() => { setCreateIncidentDialogOpen(true); }}
          >
            Incident
          </Button>
        ]}
      />

      <Typography variant="xxl" sx={{ mt: '32px' }}>Incidents</Typography>

      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          mt: '32px',
          mb: '16px'
        }}>
        <Typography
          sx={{
            color: "text.secondary",
            alignSelf: 'center',
            visibility: loading ? 'hidden' : ''
          }}>
          {incidents.length} {incidents.length === 1 ? 'result' : 'results'}
        </Typography>

        <Box sx={{ width: 300 }}>
          <OperationsSelect
            value={orderBy}
            onChange={(value) => { setOrderBy(value); }}
            options={orderByOptions}
            label="Sort"
          />
        </Box>
      </Stack>

      {
        loading ? (
          <Grid
            size={9}
            container
            sx={{
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {incidents.map((incident) => {
              return (
                <IncidentListItem
                  key={incident.id}
                  incident={incident}
                />
              );
            })}
          </>
        )
      }

      {error && (
        <Toast
          type="error"
          onClose={() => setError(null)}
        >
          {error}
        </Toast>
      )}

      <CreateIncidentDialog
        open={createIncidentDialogOpen}
        setOpen={setCreateIncidentDialogOpen}
        onCreateIncident={handleCreateIncident}
        operationId={operation?.id ?? ''}
        setError={setError}
      />

    </>
  );
};

type IncidentListItemProps = {
  incident: OperationIncident
};
const IncidentListItem: React.FC<IncidentListItemProps> = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { incident } = props;

  return (
    <Grid
      key={incident.id}
      sx={{
        marginBottom: '16px',
        borderRadius: '10px',
        border: '1px solid',
        borderColor: theme.palette.border,
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onClick={() => navigate(incident.id)}
    >
      <Stack>
        <Box
          sx={{
            backgroundColor: theme.palette.blueGrey[700],
            color: theme.palette.primary.contrastText,
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingTop: '6px',
            paddingBottom: '6px'
          }}
        >
          <Typography>{`Case Number: ${incident.case_number ?? ''}`}</Typography>
        </Box>

        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            padding: '16px'
          }}>
          <Box>
            <Typography variant="h5" component="p">{incident.title}</Typography>
          </Box>

          <Stack sx={{ minWidth: 200, gap: '16px' }}>
            <OperationDate date={incident.performed_at} />
            <Grid container spacing={1}>
              <OperationOfficialAvatar
                agency_official={incident.author.agency_official}
                variant='light'
              />
            </Grid>
          </Stack>
        </Stack>

      </Stack>
    </Grid>
  );
};

export default OperationIncidentsPage;
